const en = {
  header: {
    headerText: 'Residential Tenancies',
    subHeaderTextExplorer: 'Use this tool to explore how capital expenditures may translate into rent increases',
    subHeaderTextNoticeGenerator: 'Rent Increase Notice Generator',
    subHeaderLandlordValidator: 'Landlord Rent Increase Validator',
    subHeaderTenantValidator: 'Tenant Rent Increase Validator',
    subHeaderRentIncreaseCalculator: 'Additional Rent Increase Calculation',

    bodyHeader: 'Additional Rent Increase Explorer',
    disclaimer: `The Additional Rent Increase Explorer is a tool for estimating an additional rent increase, based on the the cost to install, replace or repair a major system or component in a residential building (capital expenditure), divided up by the number of rental units affected by the installation, replacement or repair, over a period of 120 months (10 years).  The eligible amount of the additional rent increase shown after completing all fields in the explorer is only an estimation and is not to be used to give an additional rent increase. The Residential Tenancy Branch is not bound to any results from estimations provided in the Additional Rent Increase Explorer. To apply for an additional rent increase, you must submit an on-line application to the Residential Tenancy Branch.`,
    branch: 'Residential Tenancy Branch',
    date: 'Calculated on ',
  },
  
  noticeGenerator: {
    sectionOne: {
      description: `
      You can use this tool to calculate the allowed amount of a Phase 1 additional rent increase and to determine the timeframes associated to the Phase 1 additional rent increase. 
      This tool is only used for rent increases associated to capital expenditures that the landlord has incurred under the new additional <a href="https://www2.gov.bc.ca/assets/gov/housing-and-tenancy/residential-tenancies/policy-guidelines/gl37c.pdf" target="_blank" rel="noopener noreferrer">rent increase rules</a> that came into effect on July 1, 2021. 
      To use this tool you will need the eligible rent increase amount from your Residential Tenancy Branch decision and the exact dates and rent amounts associated to current and past rent increases. 
      This tool can only be used for this Phase 1 rent increase and a specific rental unit. If you need more information on rent increases or how they are calculated, visit <a href="https://www2.gov.bc.ca/gov/content/housing-tenancy/residential-tenancies/rent-rtb/rent-increase-costs-expenses" target="_blank" rel="noopener noreferrer">our website</a>. 
      `,
      descriptionWarning: 'IMPORTANT: you must be able to print or save as PDF from your computer or device.'
    }
  },

  validator: {
    landlordDescription: `
      Landlords can use this tool if they were granted an <a href="https://www2.gov.bc.ca/assets/gov/housing-and-tenancy/residential-tenancies/policy-guidelines/gl37c.pdf" target="_blank" rel="noopener noreferrer">additional rent increase for capital expenditures</a> by the Residential Tenancy Branch and have already issued a notice for the first additional rent increase (phase 1). 
      It is necessary to validate that the phase 1 additional rent increase was imposed correctly before calculating the phase 2 additional rent increase. After validating the remaining eligible amount and timing of the phase 1 rent increase notice, landlords can use this tool to calculate the eligible amount and timeframe for serving the Phase 2 Notice of Additional Rent Increase. 
      To use this tool, you will need the eligible rent increase amount noted in the Residential Tenancy Branch decision, in addition to the dates and rent amounts of past rent increases. 
      This tool can only be used for a phase 2 additional rent increase and a specific rental unit. In other words, you cannot use this tool to calculate phase 2 rent increases for several units. 
      If you would like more information on rent increases, please visit the <a href="https://www2.gov.bc.ca/gov/content/housing-tenancy/residential-tenancies/rent-rtb/rent-increase-costs-expenses" target="_blank" rel="noopener noreferrer">Residential Tenancy Branch website</a>. 
    `,
    tenantDescription: `
      Tenants can use this tool to validate the first rent increase (phase 1) from an <a href="https://www2.gov.bc.ca/assets/gov/housing-and-tenancy/residential-tenancies/policy-guidelines/gl37c.pdf" target="_blank" rel="noopener noreferrer">additional rent increase for capital expenditures</a> granted to a landlord. 
      To use this tool, you will need the eligible rent increase amount noted in the Residential Tenancy Branch decision and the information provided on the Phase 1 Notice of Additional Rent Increase. 
      If you would like more information on rent increases, please visit the <a href="https://www2.gov.bc.ca/gov/content/housing-tenancy/residential-tenancies/rent-rtb/rent-increase-costs-expenses" target="_blank" rel="noopener noreferrer">Residential Tenancy Branch website</a>.
    `
  }, 

  step1: {
    subText: 'Step 1: Determining eligible capital expenditures',
    descriptionHTML: `<p>The first step in this process is to determine the eligible amounts that can be used towards an additional rent increase based on capital expenditures incurred.</p>
    <p>A capital expenditure is eligible for an additional rent increase if it:</p>
    <ul>
      <li>
        is made to install, repair or replace a major system or component of the residential property that required repair or replacement under 
        <a href="https://www.bclaws.gov.bc.ca/civix/document/id/complete/statreg/02078_01#section32" target="_blank" rel="noopener noreferrer">section 32 of the Residential Tenancy Act</a> or the repair or replacement
         of a major system or component with a useful life of at least five years that has either failed or is close to the end of its useful life, or
      </li>
      <li>
        achieves one or more of the following:
        <ul>
          <li>a measurable reduction in energy use or green house gas emissions</li>
          <li>improvement in the security of the residential property, and
        </ul>
      </li>
      <li>
        is not expected to recur for at least five years, and
      </li>
      <li>
        was incurred in the 18-month period preceding the month the landlord made the application.
      </li>
    </ul>
    <p>Learn more about capital expenditures <a href="https://www2.gov.bc.ca/gov/content/housing-tenancy/residential-tenancies/rent-rtb/rent-increase-costs-expenses" target="_blank" rel="noopener noreferrer">here</a>.</p>
    <p> To begin, enter the total cost of each expenditure and the count of all units that were affected by it. Enter each capital expenditure that applies to individual units click “add another capital expenditure”.</p>`,
    improvements: {
      header: 'Capital Expenditure',
      title: 'Short title of capital expenditure',
      cost: 'Total cost of capital expenditure',
      units: 'Number of all Specified Dwelling units affected by the capital expenditure, including non-rental units',
      eligibleAmount: 'Calculated eligible amount',
    },
    addImprovement: 'Add another capital expenditure',
  },
  step2: {
    subText: 'Step 2: Estimated additional rent increases for each unit',
    description1: `	
    In this step, you can determine how the additional rent increase granted based on your eligible capital expenditures can be applied with the annual allowable rent increase. If you need more information on the detailed calculations or rules, read the policy guidelines`,
    website: 'here',
    maxAllowedUrl: 'https://www2.gov.bc.ca/assets/gov/housing-and-tenancy/residential-tenancies/policy-guidelines/gl37c.pdf',

    // @params: {{cost}}
    improvementCostSingle:
      'Based on the capital expenditures provided, you may be eligible for an additional rent increase of {{cost}}. Enter the monthly rent for the unit you would like to apply an additional rent increase for and select the capital expenditures which affect that unit.',
    // @params: {{lowestCost}}, {{allCosts}}
    improvementCostMulti:
      'Based on the capital expenditures provided, you may be eligible for an additional rent increase between {{lowestCost}} (lowest single amount) and {{allCosts}} (all amounts). Enter the monthly rent for the unit you would like to apply an additional rent increase for and select the capital expenditures which affect that unit.',
    additionalInfo: {
      text:
        'Additional rent increase amounts are estimates based on the current maximum annual allowable rent increase amount, which are subject to change each year based on the Consumer Price Index. Learn more about how the ',
        urlText: 'maximum annual allowable rent increase is calculated',
        url: 'https://www2.gov.bc.ca/gov/content/housing-tenancy/residential-tenancies/rent-rtb/rent-increases',
    },
    unitRent: 'Enter current monthly rent of the selected unit',
    unitRentEntered: 'Current monthly rent of the selected unit',
    cpiValue: 'Maximum annual allowable rent increase amount',
    improvementSelect: 'Indicate the eligible capital expenditures for the selected unit',
    editImprovements: 'Edit capital expenditures',
    eligibleAmount: 'Total eligible amount',
    increaseBreakdown: {
      yearOne: 'First Year Rent Increase (for ',
      yearTwo: 'Second year additional rent increase (estimate only, as {{year}} maximum annual rent increase has not yet been set)',
      yearThree: 'Third year additional rent increase (estimate only, as {{year}} maximum annual rent increase has not yet been set)',
      increaseAmount: 'Maximum annual allowable rent increase for',
      increaseAmountFutureYears: 'Maximum annual allowable rent increase (using {{year}} rate of {{percentage}}% for estimating purposes only)',
      rentWithIncrease: 'Rent amount with annual allowable rent increase',
      additionalIncrease: 'Maximum additional rent increase allowed for unit (3.0%)',
      rentWithMaxIncrease: 'Potential maximum rent amount with all rent increases applied',
      remainingAmount1: 'Remaining eligible amount for additional rent increase in second year',
      remainingAmount2: 'Remaining eligible amount for third year',
      remainingAmount3: 'Remaining that cannot be applied',
      cpiRatesUrl: 'https://www2.gov.bc.ca/gov/content/housing-tenancy/residential-tenancies/rent-rtb/rent-increases'
    },
    print: {
      branch: 'Residential Tenancy Branch',
      // @params: {{date}}
      date: 'Calculated on {{date}}',
    },
  },
  errors: {
    title: {
      length: 'Title must be at least 2 characters',
      empty: 'Enter a title',
    },
    cost: {
      length: 'Must be greater than $1',
      empty: 'Enter a dollar amount',
    },
    units: {
      length: 'Must have at least one unit',
      empty: 'Enter a number of units',
    },
    submit: 'Press submit to calculate',
    rent: {
      length: 'Must be greater than $1',
      empty: 'Enter the rent amount',
    },
    improvement: {
      empty: 'Select Improvement',
    },
    increaseApplied: 'Select an answer',
    increaseDate: 'Select the first rent increase date',
    increaseAmount: 'Select the number of rent increases',
    eligibleAmount: {
      empty: 'Enter a valid eligible amount',
    },
    currentRent: {
      length: 'Must be greater than $1',
      empty: 'Enter a valid rent amount',
    },
    nextIncreaseDate: 'Select the rent increase date',
    increaseDateAlert: `The date that you entered is beyond 2 years which means that the landlord’s ability to apply an additional rent increase has expired. 
    To learn more about the rules for additional rent increases, visit `,
    increaseDateAlertText: 'our website',
    increaseAmountAlert: `The landlord can only implement an additional rent increase to a specific rental unit over three years. 
    To learn more about the rules for additional rent increases, visit `,
    increaseAmountAlertText: 'our website',
  },
};

export default en;